import { joinURL, withBase } from 'ufo';

/**
 * Local defineProvider — mirrors @nuxt/image/runtime's defineProvider
 * without importing it.
 *
 * Importing directly from `@nuxt/image/runtime` pulls in `useImage`,
 * which collides with Nuxt's auto-import during the Nitro esbuild step
 * (duplicate "const useImage" in the same server chunk, especially
 * under pnpm symlinks). A local version avoids the issue entirely.
 *
 * Returns a lazy factory `() => ImageProvider<T>` — exactly like the
 * original — so the Nuxt Image runtime can call `provider()` to get
 * the `{ getImage }` object.
 */
interface ImageProvider<T = Record<string, unknown>> {
	getImage: (
		src: string,
		options: T & { modifiers: Record<string, unknown> }
	) => { url: string };
}

function defineProvider<T>(
	setup: ImageProvider<T> | (() => ImageProvider<T>)
): () => ImageProvider<T> {
	let result: ImageProvider<T>;
	return () => {
		if (result) return result;
		result =
			typeof setup === 'function' ? (setup as () => ImageProvider<T>)() : setup;
		return result;
	};
}

/** Idunn service URL mode: path = CDN-friendly, query = weserv-compatible */
export type IdunnUrlMode = 'path' | 'query';

export interface IdunnProviderOptions {
	/**
	 * Base URL of your site (for resolving relative image src to absolute).
	 * Used with withBase(src, baseURL). Fallback: runtime config public.APP_URL / NUXT_BASE_URL.
	 */
	baseURL?: string;
	/**
	 * Idunn image service URL (e.g. https://images.yourdomain.com).
	 * If not set, baseURL is used as the service URL.
	 */
	idunnURL?: string;
	/** URL mode: 'path' for CDN-friendly, 'query' for weserv-compatible */
	mode?: IdunnUrlMode;
	/**
	 * When true, SVG sources are sent through Idunn for rasterisation
	 * (e.g. SVG → WebP) instead of being returned as-is.
	 */
	processSvg?: boolean;
}

/** Nuxt Image fit → Idunn fit (Idunn docs: contain → inside for "fit within bounds") */
const FIT_MAP: Record<string, string> = {
	cover: 'cover',
	contain: 'inside',
	fill: 'fill',
	inside: 'inside',
	outside: 'outside'
};

const FORMAT_MAP: Record<string, string> = {
	jpeg: 'jpg',
	jpg: 'jpg',
	png: 'png',
	webp: 'webp',
	avif: 'avif',
	gif: 'gif',
	heic: 'heic',
	tiff: 'tiff'
};

/**
 * Canonical parameter order used in BOTH query string and path modes.
 * A single ordering maximises CDN/browser cache hit ratio because the same
 * set of filters always produces the exact same URL.
 *
 * Based on the Idunn docs recommended segment order.
 */
const PARAM_ORDER = [
	'w',
	'h',
	'fit',
	'dpr',
	'output',
	'q',
	'blur',
	'sharp',
	'ro',
	'con',
	'mod',
	'bg'
] as const;

const PARAM_ORDER_SET = new Set<string>(PARAM_ORDER);

/**
 * Decode base64url (RFC 4648 §5) back to the original string.
 */
function decodeBase64Url(encoded: string): string {
	let base64 = encoded.replace(/-/g, '+').replace(/_/g, '/');
	while (base64.length % 4) base64 += '=';

	if (typeof Buffer !== 'undefined') {
		return Buffer.from(base64, 'base64').toString('utf8');
	}
	const binary = atob(base64);
	const bytes = new Uint8Array(binary.length);
	for (let i = 0; i < binary.length; i++) {
		bytes[i] = binary.charCodeAt(i);
	}
	return new TextDecoder().decode(bytes);
}

/**
 * If `src` is already an Idunn-processed URL (same serviceURL origin),
 * extract the original source to avoid double-processing.
 *
 * Handles both URL modes:
 * - path:  <serviceURL>/<base64url_source>/w:800/output:webp/.../image.ext
 * - query: <serviceURL>/?url=<original_url>&w=800&output=webp
 */
function extractOriginalSrc(src: string, serviceURL: string): string | null {
	if (!serviceURL || !src.startsWith(serviceURL)) return null;

	try {
		const parsed = new URL(src);
		const rawUrl = parsed.searchParams.get('url');
		if (rawUrl) return rawUrl;
	} catch {
		// Not a valid URL — fall through to path check
	}

	const pathAfterService = src.slice(serviceURL.replace(/\/$/, '').length);
	const segments = pathAfterService.split('/').filter(Boolean);
	const base64Segment = segments[0];
	if (!base64Segment) return null;

	try {
		const decoded = decodeBase64Url(base64Segment);
		if (decoded.startsWith('http://') || decoded.startsWith('https://')) {
			return decoded;
		}
	} catch {
		// Not a valid base64 segment — not an Idunn URL
	}

	return null;
}

/**
 * Encode URL as base64url (RFC 4648 §5).
 * Safe for non-ASCII characters on both Node and browser.
 */
function encodeBase64Url(url: string): string {
	let base64: string;
	if (typeof Buffer !== 'undefined') {
		base64 = Buffer.from(url, 'utf8').toString('base64');
	} else {
		// Browser: handle UTF-8 via TextEncoder → binary string → btoa
		const bytes = new TextEncoder().encode(url);
		let binary = '';
		for (const byte of bytes) {
			binary += String.fromCharCode(byte);
		}
		base64 = btoa(binary);
	}
	return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function mapFit(nuxtFit: string): string {
	return FIT_MAP[nuxtFit] ?? 'inside';
}

/**
 * Collect Idunn params and boolean flags from Nuxt Image modifiers.
 * Single source of truth for both query and path modes — prevents key leakage and duplication.
 */
function buildIdunnParams(modifiers: Record<string, unknown>): {
	params: Record<string, string>;
	flags: string[];
} {
	const params: Record<string, string> = {};
	const flags: string[] = [];
	const m = modifiers ?? {};

	const width = m.width as number | undefined;
	const height = m.height as number | undefined;
	const format = m.format as string | undefined;
	const quality = m.quality as number | undefined;
	const fit = m.fit as string | undefined;

	if (width != null && Number(width) > 0) params.w = String(width);
	if (height != null && Number(height) > 0) params.h = String(height);
	if (format) params.output = FORMAT_MAP[format] ?? format;
	if (quality != null && Number(quality) > 0) params.q = String(quality);
	if (fit) params.fit = mapFit(fit);

	const dpr = (m.pixelDensity ?? m.dpr) as number | undefined;
	if (dpr != null && Number(dpr) > 1 && Number(dpr) <= 8)
		params.dpr = String(dpr);

	const blur = m.blur as number | undefined;
	if (blur != null && !Number.isNaN(Number(blur))) params.blur = String(blur);

	const sharp = m.sharpen as number | undefined;
	if (sharp != null && !Number.isNaN(Number(sharp)))
		params.sharp = String(sharp);

	const ro = (m.rotate as number | undefined) ?? (m.ro as number | undefined);
	if (ro != null && !Number.isNaN(Number(ro))) params.ro = String(ro);

	const con = m.contrast as number | undefined;
	if (con != null && !Number.isNaN(Number(con))) params.con = String(con);

	const mod = m.modulate as string | undefined;
	if (mod) params.mod = mod;

	const bg = m.background as string | undefined;
	if (bg) params.bg = bg.replace(/^#/, '');

	if (m.flip === true || m.flip === 'true') flags.push('flip');
	if (m.flop === true || m.flop === 'true' || m.mirror === true)
		flags.push('flop');
	if (m.we === true || m.we === 'true' || m.withoutEnlargement === true)
		flags.push('we');

	return { params, flags };
}

/**
 * Return params as `[key, value]` pairs in canonical order.
 * Keys listed in PARAM_ORDER come first (in that exact sequence),
 * followed by any extra keys sorted alphabetically.
 * This guarantees the same set of filters → same URL in every call.
 */
function sortedParamEntries(
	params: Record<string, string>
): [string, string][] {
	const entries: [string, string][] = [];

	// 1. Known keys in canonical order
	for (const key of PARAM_ORDER) {
		const value = params[key];
		if (value != null && value !== '') entries.push([key, value]);
	}

	// 2. Any remaining keys sorted alphabetically (deterministic fallback)
	const extra = Object.keys(params)
		.filter(
			k => !PARAM_ORDER_SET.has(k) && params[k] != null && params[k] !== ''
		)
		.sort();
	for (const key of extra) {
		entries.push([key, params[key]!]);
	}

	return entries;
}

/**
 * Sort boolean flags alphabetically so they always appear in the same order.
 */
function sortedFlags(flags: string[]): string[] {
	return [...flags].sort();
}

/**
 * Derive the filename from the original image URL.
 * The name comes from the source URL; only the extension is replaced
 * when an `output` filter is present, otherwise the original extension is kept.
 * e.g. `e32ecbba-44e8-80ca-8b012b876868.avif`, `photo.webp`
 */
function deriveFilename(params: Record<string, string>, src: string): string {
	const pathname = (src.split(/[?#]/)[0] ?? '').split('/').pop() ?? '';
	const dotIdx = pathname.lastIndexOf('.');
	const name = dotIdx > 0 ? pathname.slice(0, dotIdx) : pathname || 'image';
	const srcExt = dotIdx > 0 ? pathname.slice(dotIdx + 1).toLowerCase() : 'jpg';
	const ext = params.output || srcExt;
	return `${name}.${ext}`;
}

/** Build CDN-friendly path URL: /<base64url_source>/w:800/output:webp/.../image.avif */
function buildPathUrl(
	serviceURL: string,
	src: string,
	params: Record<string, string>,
	flags: string[]
): string {
	const encodedSrc = encodeBase64Url(src);
	const segments: string[] = [encodedSrc];

	for (const [key, value] of sortedParamEntries(params)) {
		segments.push(`${key}:${value}`);
	}
	segments.push(...sortedFlags(flags));
	segments.push(deriveFilename(params, src));

	return joinURL(serviceURL, ...segments);
}

/**
 * Build weserv-compatible query string URL: ?url=<src>&w=800&output=webp&we
 *
 * Parameters follow the same canonical order as path mode.
 * The source URL is percent-encoded via URLSearchParams so that `?` and `&`
 * in the original URL don't break the query string parsing.
 */
function buildQueryUrl(
	serviceURL: string,
	src: string,
	params: Record<string, string>,
	flags: string[]
): string {
	const search = new URLSearchParams();
	search.set('url', src);

	for (const [key, value] of sortedParamEntries(params)) {
		search.set(key, value);
	}
	for (const flag of sortedFlags(flags)) {
		search.append(flag, '');
	}

	const qs = search.toString();
	return qs ? `${serviceURL.replace(/\/?$/, '')}/?${qs}` : serviceURL;
}

export default defineProvider<IdunnProviderOptions>({
	getImage(
		src,
		{ modifiers = {}, baseURL, idunnURL, mode: rawMode, processSvg }
	) {
		const rawSrc = typeof src === 'string' ? src.trim() : '';
		if (!rawSrc) return { url: '' };

		if (rawSrc.startsWith('data:')) {
			return { url: rawSrc };
		}

		const isSvg = rawSrc.endsWith('.svg') || rawSrc.includes('.svg?');

		if (isSvg && !processSvg) {
			return { url: rawSrc };
		}

		let siteBaseURL = baseURL ?? '';
		if (!siteBaseURL) {
			try {
				const config =
					typeof useRuntimeConfig !== 'undefined' ? useRuntimeConfig() : null;
				if (config) {
					siteBaseURL =
						(config.public?.APP_URL as string) ||
						(config.public?.NUXT_BASE_URL as string) ||
						'';
				}
			} catch {
				// ignore — useRuntimeConfig may not be available outside Nuxt context
			}
		}

		const serviceURL = idunnURL ?? baseURL ?? '';
		const mode: IdunnUrlMode = (rawMode as IdunnUrlMode) ?? 'path';

		if (!serviceURL) return { url: rawSrc };

		let absoluteSrc = withBase(rawSrc, siteBaseURL);

		const originalSrc = extractOriginalSrc(absoluteSrc, serviceURL);
		if (originalSrc) {
			absoluteSrc = originalSrc;
		}

		const { params, flags } = buildIdunnParams(
			modifiers as Record<string, unknown>
		);

		const url =
			mode === 'query'
				? buildQueryUrl(serviceURL, absoluteSrc, params, flags)
				: buildPathUrl(serviceURL, absoluteSrc, params, flags);

		return { url };
	}
});
